import { z } from 'zod';
import { SpravaOUcastiVerejnostiFormData }
  from '../../../../model/sprievodnyDokument/spravaoUcastiVerejnosti/SpravaOUcastiVerejnostiFormData';
import { ClientStatus } from '../../../../model/ClientStatus';
import { useClientFetch } from '../../../../context/ClientStatusContext';
import getFormData from '../../../../function/getFormData';
import { FormDataResponse } from '../../../../model/sprievodnyDokument/FormDataResponse';
import fallback from '../../../../function/zodFallback';

interface ReturnType {
  formDataResponse: FormDataResponse<SpravaOUcastiVerejnostiFormData>,
  formDataStatus: ClientStatus,
}

const Response = z.object({
  informovanie: fallback(z.boolean(), false),
  prerokovanie: fallback(z.boolean(), false),
  odovodnenieZapojenieVerejnosti: fallback(z.string(), ''),
  predbeznaInformacia: fallback(z.boolean(), false),

  legislativnyZamer: fallback(z.boolean(), false),
  ineSposobInformovaniaVerejnosti: fallback(z.boolean(), false),
  ineSposobInformovaniaVerejnostiText: fallback(z.string(), ''),

  oprobleme: fallback(z.boolean(), false),
  osposobeZapojenia: fallback(z.boolean(), false),
  ocasovomRamci: fallback(z.boolean(), false),
  oprocese: fallback(z.boolean(), false),
  osposobeNalozenia: fallback(z.boolean(), false),
  ineInformacieVerejnosti: fallback(z.boolean(), false),
  ineInformacieVerejnostiText: fallback(z.string(), ''),

  formaOsobne: fallback(z.boolean(), false),
  formaUstne: fallback(z.boolean(), false),
  formaPisomne: fallback(z.boolean(), false),
  formaIne: fallback(z.boolean(), false),
  formaIneText: fallback(z.string(), ''),
  formaOdovodnenie: fallback(z.string(), ''),

  pracovnaSkupina: fallback(z.boolean(), false),
  konferencia: fallback(z.boolean(), false),
  diskusiaKLegislativnemuProcesu: fallback(z.boolean(), false),
  konzultacia: fallback(z.boolean(), false),
  pripomienkovanie: fallback(z.boolean(), false),
  inePrerokovanieVerejnostou: fallback(z.boolean(), false),
  inePrerokovanieVerejnostouText: fallback(z.string(), ''),

  vyzvaneZaujmoveZdruzenia: fallback(z.number(), undefined),
  vyzvanePodnikatelia: fallback(z.number(), undefined),
  vyzvaneMimovladneANeziskoveOrg: fallback(z.number(), undefined),
  vyzvaneAkademickaAVedeckaObec: fallback(z.number(), undefined),
  vyzvaneCirkvi: fallback(z.number(), undefined),
  vyzvaneIne: fallback(z.number(), undefined),
  vyzvaneIneText: fallback(z.string(), ''),

  zucastneneZaujmoveZdruzenia: fallback(z.number(), undefined),
  zucastnenePodnikatelia: fallback(z.number(), undefined),
  zucastneneMimovladneANeziskoveOrg: fallback(z.number(), undefined),
  zucastneneAkademickaAVedeckaObec: fallback(z.number(), undefined),
  zucastneneCirkvi: fallback(z.number(), undefined),
  zucastneneIne: fallback(z.number(), undefined),
  zucastneneIneText: fallback(z.string(), ''),

  vyzvaneOdovodnenie: fallback(z.string(), ''),

  prejaviliZaujemZaujmoveZdruzenia: fallback(z.number(), undefined),
  prejaviliZaujemPodnikatelia: fallback(z.number(), undefined),
  prejaviliZaujemMimovladneANeziskoveOrg: fallback(z.number(), undefined),
  prejaviliZaujemAkademickaAVedeckaObec: fallback(z.number(), undefined),
  prejaviliZaujemCirkvi: fallback(z.number(), undefined),
  prejaviliZaujemIne: fallback(z.number(), undefined),
  prejaviliZaujemIneText: fallback(z.string(), ''),

  iniciativneZucastneneZaujmoveZdruzenia: fallback(z.number(), undefined),
  iniciativneZucastnenePodnikatelia: fallback(z.number(), undefined),
  iniciativneZucastneneMimovladneANeziskoveOrg: fallback(z.number(), undefined),
  iniciativneZucastneneAkademickaAVedeckaObec: fallback(z.number(), undefined),
  iniciativneZucastneneCirkvi: fallback(z.number(), undefined),
  iniciativneZucastneneIne: fallback(z.number(), undefined),
  iniciativneZucastneneIneText: fallback(z.string(), ''),

  organyVerejnejSpravy: fallback(z.boolean(), undefined),
  zaujmoveZdruzenia: fallback(z.boolean(), undefined),
  podnikatelia: fallback(z.boolean(), undefined),
  mimovladneANeziskoveOrg: fallback(z.boolean(), undefined),
  akademickaAVedeckaObec: fallback(z.boolean(), undefined),
  cirkvi: fallback(z.boolean(), undefined),
  ine: fallback(z.boolean(), undefined),
  ineText: fallback(z.string(), ''),
  sposobNalozeniaOdovodnenie: fallback(z.string(), ''),
  vyhodnotenieUcastiOdovodnenie: fallback(z.string(), ''),
}).partial();

function map(rawFormData: object): SpravaOUcastiVerejnostiFormData {
  const formData = Response.parse(rawFormData);

  return {
    informovanie: formData.informovanie,
    prerokovanie: formData.prerokovanie,
    odovodnenieZapojenieVerejnosti: formData.odovodnenieZapojenieVerejnosti,
    predbeznaInformacia: formData.predbeznaInformacia,

    legislativnyZamer: formData.legislativnyZamer,
    ineSposobInformovaniaVerejnosti: formData.ineSposobInformovaniaVerejnosti,
    ineSposobInformovaniaVerejnostiText: formData.ineSposobInformovaniaVerejnostiText,

    oprobleme: formData.oprobleme,
    osposobeZapojenia: formData.osposobeZapojenia,
    ocasovomRamci: formData.ocasovomRamci,
    oprocese: formData.oprocese,
    osposobeNalozenia: formData.osposobeNalozenia,
    ineInformacieVerejnosti: formData.ineInformacieVerejnosti,
    ineInformacieVerejnostiText: formData.ineInformacieVerejnostiText,

    formaOsobne: formData.formaOsobne,
    formaUstne: formData.formaUstne,
    formaPisomne: formData.formaPisomne,
    formaIne: formData.formaIne,
    formaIneText: formData.formaIneText,
    formaOdovodnenie: formData.formaOdovodnenie,

    pracovnaSkupina: formData.pracovnaSkupina,
    konferencia: formData.konferencia,
    diskusiaKLegislativnemuProcesu: formData.diskusiaKLegislativnemuProcesu,
    konzultacia: formData.konzultacia,
    pripomienkovanie: formData.pripomienkovanie,
    inePrerokovanieVerejnostou: formData.inePrerokovanieVerejnostou,
    inePrerokovanieVerejnostouText: formData.inePrerokovanieVerejnostouText,

    vyzvane: {
      zaujmoveZdruzenia: formData.vyzvaneZaujmoveZdruzenia,
      podnikatelia: formData.vyzvanePodnikatelia,
      mimovladneANeziskoveOrg: formData.vyzvaneMimovladneANeziskoveOrg,
      akademickaAVedeckaObec: formData.vyzvaneAkademickaAVedeckaObec,
      cirkvi: formData.vyzvaneCirkvi,
      ine: formData.vyzvaneIne,
      ineText: formData.vyzvaneIneText,
    },
    zucastnene: {
      zaujmoveZdruzenia: formData.zucastneneZaujmoveZdruzenia,
      podnikatelia: formData.zucastnenePodnikatelia,
      mimovladneANeziskoveOrg: formData.zucastneneMimovladneANeziskoveOrg,
      akademickaAVedeckaObec: formData.zucastneneAkademickaAVedeckaObec,
      cirkvi: formData.zucastneneCirkvi,
      ine: formData.zucastneneIne,
      ineText: formData.zucastneneIneText,
    },
    vyzvaneOdovodnenie: formData.vyzvaneOdovodnenie,
    prejaviliZaujem: {
      zaujmoveZdruzenia: formData.prejaviliZaujemZaujmoveZdruzenia,
      podnikatelia: formData.prejaviliZaujemPodnikatelia,
      mimovladneANeziskoveOrg: formData.prejaviliZaujemMimovladneANeziskoveOrg,
      akademickaAVedeckaObec: formData.prejaviliZaujemAkademickaAVedeckaObec,
      cirkvi: formData.prejaviliZaujemCirkvi,
      ine: formData.prejaviliZaujemIne,
      ineText: formData.prejaviliZaujemIneText,
    },
    iniciativneZucastnene: {
      zaujmoveZdruzenia: formData.iniciativneZucastneneZaujmoveZdruzenia,
      podnikatelia: formData.iniciativneZucastnenePodnikatelia,
      mimovladneANeziskoveOrg: formData.iniciativneZucastneneMimovladneANeziskoveOrg,
      akademickaAVedeckaObec: formData.iniciativneZucastneneAkademickaAVedeckaObec,
      cirkvi: formData.iniciativneZucastneneCirkvi,
      ine: formData.iniciativneZucastneneIne,
      ineText: formData.iniciativneZucastneneIneText,
    },

    organyVerejnejSpravy: formData.organyVerejnejSpravy,
    zaujmoveZdruzenia: formData.zaujmoveZdruzenia,
    podnikatelia: formData.podnikatelia,
    mimovladneANeziskoveOrg: formData.mimovladneANeziskoveOrg,
    akademickaAVedeckaObec: formData.akademickaAVedeckaObec,
    cirkvi: formData.cirkvi,
    ine: formData.ine,
    ineText: formData.ineText,
    sposobNalozeniaOdovodnenie: formData.sposobNalozeniaOdovodnenie,
    vyhodnotenieUcastiOdovodnenie: formData.vyhodnotenieUcastiOdovodnenie,
  };
}

export default (
  type: string,
  year: number,
  index: number,
  stadiumUuid: string,
): ReturnType => {
  const {
    data,
    status,
  } = useClientFetch(
    getFormData,
    [type, year, index, 'SpravaOUcastiVerejnosti', stadiumUuid],
    [],
  );
  return {
    formDataResponse: data ? {
      formData: map(data.formData),
      materialDetail: data.materialDetail,
    } : null,
    formDataStatus: status,
  };
};
